import React, { Component } from "react";
import PropTypes from "prop-types";
import { line, curveStepBefore } from "d3-shape";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas } from "../GenericComponent";
import { isDefined, isNotDefined } from "../utils";

class KagiSeries extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
  }
  drawOnCanvas(ctx, moreProps) {
    const { xAccessor, xScale, chartConfig, plotData } = moreProps;
    const { yScale } = chartConfig || {};

    // Guard: don't draw if scales or plotData are not ready
    if (!xScale || !yScale || !plotData || plotData.length === 0) {
      return;
    }

    drawOnCanvas(ctx, this.props, moreProps, xAccessor);
  }
  render() {
    return (
      <GenericChartComponent
        svgDraw={this.renderSVG}
        canvasToDraw={getAxisCanvas}
        canvasDraw={this.drawOnCanvas}
        drawOn={["pan", "drag", "mousemove"]}
      />
    );
  }
  renderSVG(moreProps) {
    const { xAccessor, xScale, chartConfig, plotData } = moreProps;
    const { yScale } = chartConfig || {};

    const { className, stroke, fill, strokeWidth } = this.props;

    // Guard: don't render if scales or plotData are not ready
    if (!xScale || !yScale || !plotData || plotData.length === 0) {
      return null;
    }

    const paths = helper(plotData, xAccessor);

    if (!paths || paths.length === 0) {
      return null;
    }

    const pathElements = paths
      .map((each, i) => {
        if (!each.plot || each.plot.length === 0) {
          return null;
        }

        const dataSeries = line()
          .x((item) => xScale(item[0]))
          .y((item) => yScale(item[1]))
          .curve(curveStepBefore);

        const pathData = dataSeries(each.plot);

        if (!pathData) {
          return null;
        }

        return (
          <path
            key={i}
            d={pathData}
            className={each.type}
            stroke={stroke[each.type]}
            fill={fill[each.type]}
            strokeWidth={strokeWidth}
          />
        );
      })
      .filter(Boolean);

    return <g className={className}>{pathElements}</g>;
  }
}
KagiSeries.propTypes = {
  className: PropTypes.string,
  stroke: PropTypes.object,
  fill: PropTypes.object,
  strokeWidth: PropTypes.number.isRequired,
};

KagiSeries.defaultProps = {
  className: "react-stockcharts-kagi",
  strokeWidth: 2,
  stroke: {
    yang: "#6BA583",
    yin: "#E60000",
  },
  fill: {
    yang: "none",
    yin: "none",
  },
  currentValueStroke: "#000000",
};

function drawOnCanvas(ctx, props, moreProps, xAccessor) {
  const { stroke, strokeWidth, currentValueStroke } = props;
  const {
    xScale,
    chartConfig: { yScale },
    plotData,
  } = moreProps;

  const paths = helper(plotData, xAccessor);

  if (!paths || paths.length === 0) {
    return;
  }

  paths.forEach((each) => {
    if (!each.plot || each.plot.length === 0) {
      return;
    }

    ctx.strokeStyle = stroke[each.type];
    ctx.lineWidth = each.type === "yang" ? strokeWidth * 2 : strokeWidth;

    //	ctx.lineWidth = strokeWidth;
    ctx.beginPath();

    let prevX;
    let isFirstPoint = true;

    each.plot.forEach((d) => {
      const [x, y] = [xScale(d[0]), yScale(d[1])];
      if (isFirstPoint) {
        ctx.moveTo(x, y);
        isFirstPoint = false;
      } else {
        if (isDefined(prevX)) {
          ctx.lineTo(prevX, y);
        }
        ctx.lineTo(x, y);
      }
      prevX = x;
    });
    ctx.stroke();
  });

  // Draw current value indicators if available
  if (paths.length > 0) {
    const lastPlot = paths[paths.length - 1].plot;
    if (lastPlot && lastPlot.length > 0) {
      const last = lastPlot[lastPlot.length - 1];
      if (last && last.length > 3) {
        ctx.lineWidth = 1;
        const [x, y1, y2] = [xScale(last[0]), yScale(last[2]), yScale(last[3])];

        ctx.beginPath();
        ctx.moveTo(x, y1);
        ctx.lineTo(x + 10, y1);
        ctx.stroke();

        ctx.beginPath();
        ctx.strokeStyle = currentValueStroke;
        ctx.moveTo(x - 10, y2);
        ctx.lineTo(x, y2);
        ctx.stroke();
      }
    }
  }
}

function helper(plotData, xAccessor) {
  if (!plotData || plotData.length === 0) {
    return [];
  }

  const kagiLine = [];
  let kagi = {};
  let d = plotData[0];
  let idx = xAccessor(d);

  for (let i = 0; i < plotData.length; i++) {
    d = plotData[i];

    if (isNotDefined(d.close)) continue;
    if (isNotDefined(kagi.type)) kagi.type = d.startAs;
    if (isNotDefined(kagi.plot)) kagi.plot = [];

    idx = xAccessor(d);
    kagi.plot.push([idx, d.open]);

    if (isDefined(d.changeTo)) {
      kagi.plot.push([idx, d.changePoint]);
      kagi.added = true;
      kagiLine.push(kagi);

      kagi = {
        type: d.changeTo,
        plot: [],
        added: false,
      };
      kagi.plot.push([idx, d.changePoint]);
    }
  }

  if (!kagi.added) {
    kagi.plot.push([idx, d.close, d.current, d.reverseAt]);
    kagiLine.push(kagi);
  }

  return kagiLine;
}

export default KagiSeries;
